// Remove this file after the Account_Provisioning_History release.
import {useCallback, useEffect, useRef} from 'react';


import {getAccountActiveUserHistoryEventsByDate} from 'src/openapi/admin/apis/GetAccountActiveUserHistoryEventsApi';
import {useAppDispatch, useAppSelector} from 'src/store/hooks';

import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {
    selectAccountActiveUserHistoryEventsEntry,
    selectAccountActiveUserHistoryEventsIsFresh,
} from './services/account-active-user-history-events-slice';

type Props = {
    accountId: number,
    historyDate: string,
    /**
     * If true (default), the hook will load on mount when cache is empty.
     * Set false to fully control loading via `load()`.
     */
    shouldLoad?: boolean,
};

/**
 * Fetches `/web-api/accounts/:accountId/active-user-history/:historyDate/events` and caches results in Redux.
 *
 * Cache policy: 5-minute TTL, in-memory cache for the current session.
 */
export const useAccountActiveUserHistoryEventsByDate = ({
    accountId,
    historyDate,
    shouldLoad = true,
}: Props) => {
    const dispatch = useAppDispatch();
    const {createAbortSignal} = useAbortSignal();

    const entry = useAppSelector(state =>
        selectAccountActiveUserHistoryEventsEntry(state, accountId, historyDate),
    );

    const isFresh = useAppSelector(state =>
        selectAccountActiveUserHistoryEventsIsFresh(state, accountId, historyDate),
    );

    const isFreshRef = useRef(isFresh);
    isFreshRef.current = isFresh;

    const load = useCallback(async (opts?: {refresh?: boolean}) => {
        const refresh = opts?.refresh ?? false;
        if (!refresh && isFreshRef.current) {
            return;
        }

        await dispatch(getAccountActiveUserHistoryEventsByDate(
            {accountId, historyDate},
            {signal: createAbortSignal()},
        ));
    }, [dispatch, accountId, historyDate, createAbortSignal]);

    useEffect(() => {
        if (shouldLoad && !entry?.isLoading && !isFresh) {
            load();
        }
    }, [shouldLoad, entry?.isLoading, isFresh, load]);

    return {
        data: entry?.data ?? null,
        isLoading: entry?.isLoading ?? (shouldLoad && !isFresh),
        error: entry?.error ?? null,
        load,
    };
};
